import {
  createProperty,
  createPropertiesBatch,
  propertyFromResolveProperty,
  resolvePropertiesBatch,
} from "../../../../../../../../../../../../../../../api/kehrblatt/property-registry/properties"
import {
  formatAddressInline,
  normalizeCountryLabel,
} from "../../../../../../../../../../../../../../../_shared/_logic/countries"
import { formatPropertyAddressInlineWithUnit } from "../../../../../../../../../../../../../../../_shared/_logic/unit-labels"

export const hasResidentialUnits = (
  property?: { units?: Array<{ unit_type?: string | null }> | null } | null,
) => (property?.units ?? []).some((unit) => unit?.unit_type === "RESIDENTIAL")

export const isBuildingOnlyProperty = (
  property?: { units?: Array<{ unit_type?: string | null }> | null } | null,
) => {
  const units = property?.units ?? []

  if (!units.length) return false

  return units.every((unit) => unit?.unit_type === "BUILDING")
}

const normalizeAddressToken = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase().replace(/\s+/g, " ")

const houseNumberPattern = /^\d+(?:\s*(?:[-/]\s*|\s+)\d+)*(?:\s*\/?\s*[a-zäöüß])?$/i

const normalizeHouseNumberToken = (value: string | null | undefined) => {
  const normalized = normalizeAddressToken(value)

  if (!normalized || !houseNumberPattern.test(normalized)) return normalized

  return normalized
    .replace(/\s*([-/])\s*/g, "$1")
    .replace(/(\d)\s+(\d)/g, "$1/$2")
    .replace(/(\d)\s+([a-zäöüß])($|[-/])/gi, "$1$2$3")
    .replace(/(\d)\/([a-zäöüß])($|[-/])/gi, "$1$2$3")
    .toLowerCase()
}

const normalizeStreetToken = (value: string | null | undefined) =>
  normalizeAddressToken(value)
    .replace(/ß/g, "ss")
    .replace(/str\.(?=[\s,\d]|$)/g, "strasse")
    .replace(/str(?=[\s,\d]|$)/g, "strasse")
    .replace(/strasse(?=[\s,\d]|$)/g, "strasse")

const normalizeCountryToken = (value: string | null | undefined) => {
  return normalizeAddressToken(normalizeCountryLabel(value))
}

const normalizeAddressExtraToken = (value: string | null | undefined) => {
  const normalized = normalizeAddressToken(value)

  if (!normalized) return ""

  // Ignore technical import metadata, so ISI lookups still match existing addresses.
  const kept = normalized
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part && !part.startsWith("import "))

  return kept.join(" | ")
}

export const normalizeKey = (entry: {
  street?: string | null
  house_number?: string | null
  zip?: string | null
  city?: string | null
  country?: string | null
  address_extra?: string | null
}) => {
  const parts = [
    normalizeStreetToken(entry.street),
    normalizeHouseNumberToken(entry.house_number),
    normalizeAddressToken(entry.zip),
    normalizeAddressToken(entry.city),
    normalizeCountryToken(entry.country),
    normalizeAddressExtraToken(entry.address_extra),
  ].join("|")

  return parts
}

export const mergeProperties = (list: Property[], extra: Property[]) => {
  const byId = new Map<string, Property>()
  list.forEach((entry) => entry?.id && byId.set(entry.id, entry))
  extra.forEach((entry) => entry?.id && byId.set(entry.id, entry))

  return Array.from(byId.values())
}

export async function uploadAddresses(
  createPayload: Array<Partial<PropertyCreatePayload>>,
  properties: Property[] = [],
  organizationId: string,
) {
  // Try new batch endpoint to avoid hundreds of single requests; fall back to legacy flow if unavailable.
  try {
    const batchPayload: PropertyCreatePayload[] = createPayload.map((row) => {
      const units = row.units ?? []

      return {
        organization_id: organizationId,
        street: row.street || "",
        house_number: row.house_number ?? null,
        zip: row.zip ?? null,
        city: row.city ?? null,
        country: row.country ?? null,
        address_extra: row.address_extra ?? null,
        geo_lat: row.geo_lat ?? null,
        geo_lng: row.geo_lng ?? null,
        units,
      }
    })

    const result = await createPropertiesBatch(batchPayload)
    const propertiesFromBatch = Array.isArray(result?.properties) ? result.properties : []
    const createdIdSet = new Set(result?.created_ids ?? [])

    return {
      nextSelection: propertiesFromBatch,
      createdProps: propertiesFromBatch.filter((prop) => createdIdSet.has(prop.id)),
      existing: mergeProperties(properties, propertiesFromBatch),
    }
  } catch (batchError) {
    console.error("batch_property_create_failed_fallback", batchError)
  }

  const existingResponse = await resolvePropertiesBatch(
    createPayload.map((row) => ({
      organization_id: organizationId,
      street: row.street || "",
      house_number: row.house_number ?? null,
      zip: row.zip ?? null,
      city: row.city ?? null,
      country: row.country ?? null,
      address_extra: row.address_extra ?? null,
      geo_lat: row.geo_lat ?? null,
      geo_lng: row.geo_lng ?? null,
      units: row.units ?? [],
    })),
  )
  const resolvedProperties = (existingResponse.properties ?? []).map(propertyFromResolveProperty)
  const existing = mergeProperties(properties, resolvedProperties)
  const lookup = new Map(existing.map((entry) => [normalizeKey(entry), entry]))

  const nextSelection: Property[] = []
  const createdProps: Property[] = []

  for (const row of createPayload) {
    const key = normalizeKey(row)
    const already = lookup.get(key)

    if (already) {
      nextSelection.push(already)
      continue
    }

    const units = row.units ?? []
    const createdProperty = await createProperty({
      organization_id: organizationId,
      street: row.street || "",
      house_number: row.house_number ?? null,
      zip: row.zip ?? null,
      city: row.city ?? null,
      country: row.country ?? null,
      address_extra: row.address_extra ?? null,
      geo_lat: row.geo_lat ?? null,
      geo_lng: row.geo_lng ?? null,
      units,
    })
    lookup.set(key, createdProperty)
    createdProps.push(createdProperty)
    nextSelection.push(createdProperty)
  }

  return { nextSelection, createdProps, existing }
}

export const formatAddress = (property: Property, propertyUnitId?: string | null) => {
  return formatPropertyAddressInlineWithUnit(property, propertyUnitId) || "Unbekannte Liegenschaft"
}

export const formatAddressPayload = (payload: Partial<PropertyCreatePayload>) => {
  return formatAddressInline(payload) || "Unbekannte Liegenschaft"
}
